import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  runTransaction,
  serverTimestamp,
  Timestamp,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  ANNUAL_INCOME_OPTIONS,
  REGION_PREFECTURES,
  type RegionName,
  type User,
  type UserRole,
  type UserStatus,
} from '@kulum/shared';

export interface UserFilters {
  role?: UserRole;
  status?: UserStatus;
  searchQuery?: string;
  isDummy?: 'only' | 'exclude';
  region?: RegionName;
  prefecture?: string;
  ageMin?: number;
  ageMax?: number;
  heightMin?: number;
  heightMax?: number;
  educationLevel?: string;
  annualIncomeMin?: string;
  bloodType?: string;
  bodyType?: string;
  maritalStatus?: string;
  donationMethod?: 'syringe' | 'timing';
  donationArea?: string;
  travelExpense?: string;
  identityDisclosure?: string;
  identityVerified?: boolean;
  educationVerified?: boolean;
  incomeVerified?: boolean;
  spermTestVerified?: boolean;
  stdTestVerified?: boolean;
  hasReviews?: boolean;
  minReviewRating?: number;
}

export interface UserListResult {
  users: User[];
  totalCount: number;
  totalPages: number;
}

type UnknownRecord = Record<string, unknown>;

type VerificationFilterKey = 'identity' | 'education' | 'income' | 'spermTest' | 'stdTest';

interface UserProfileFilterData {
  residence?: string;
  age?: number;
  height?: number;
  educationLevel?: string;
  annualIncome?: string;
  bloodType?: string;
  bodyType?: string;
  maritalStatus?: string;
  donationMethods: string[];
  donationAreas: string[];
  travelExpense?: string;
  identityDisclosure?: string;
  verifications: Record<VerificationFilterKey, boolean>;
}

interface UserReviewStats {
  averageRating: number;
  totalCount: number;
}

export interface UserWithAdminMeta extends User {
  suspensionReason?: string;
  suspendedAt?: Date;
  suspendedBy?: string;
  statusBeforeSuspension?: UserStatus;
}

export interface AdminUserProfile {
  residence?: string;
  birthDate?: Date;
  age?: number;
  basicInfo: UnknownRecord;
  physicalInfo?: UnknownRecord;
  donationPolicy?: UnknownRecord;
  doneePolicy?: UnknownRecord;
}

export interface AdminPrivateProfile {
  freeText?: string;
  images: string[];
}

export interface AdminUserVerification {
  adminReview?: UnknownRecord;
  items: Record<string, { status: string; documents: number; rank?: string; expiresAt?: Date }>;
}

export interface AdminUserInquiry {
  id: string;
  category: string;
  status: string;
  message: string;
  createdAt?: Date;
}

export interface AdminUserAccessRequest {
  id: string;
  direction: 'sent' | 'received';
  counterpartyId: string;
  counterpartyName: string;
  status: string;
  message?: string;
  requestedAt?: Date;
  respondedAt?: Date;
}

export interface AdminUserConversation {
  id: string;
  counterpartyId: string;
  counterpartyName: string;
  lastMessage?: string;
  lastMessageAt?: Date;
  unreadCount: number;
}

export interface AdminUserReview {
  id: string;
  direction: 'sent' | 'received';
  counterpartyId: string;
  counterpartyName: string;
  rating: number;
  comment?: string;
  createdAt?: Date;
}

export interface AdminUserNotification {
  id: string;
  type: string;
  title: string;
  status?: UnknownRecord;
  sentAt?: Date;
  readAt?: Date;
}

export interface AdminUserOperationDetail {
  user: UserWithAdminMeta;
  profile: AdminUserProfile | null;
  privateProfile: AdminPrivateProfile | null;
  verification: AdminUserVerification | null;
  inquiries: AdminUserInquiry[];
  accessRequests: AdminUserAccessRequest[];
  conversations: AdminUserConversation[];
  reviews: AdminUserReview[];
  notifications: AdminUserNotification[];
}

const USER_STATUSES: UserStatus[] = [
  'pending',
  'email_verified',
  'under_review',
  'approved',
  'rejected',
  'suspended',
  'withdrawn',
];

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const asOptionalString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const asOptionalNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

const asDate = (value: unknown): Date | undefined => {
  if (value && typeof value === 'object' && 'toDate' in value) {
    const toDate = (value as { toDate?: unknown }).toDate;
    if (typeof toDate === 'function') {
      return toDate.call(value) as Date;
    }
  }
  return undefined;
};

const asUserStatus = (value: unknown): UserStatus | undefined =>
  USER_STATUSES.includes(value as UserStatus) ? (value as UserStatus) : undefined;

const calculateAge = (birthDate?: Date): number | undefined => {
  if (!birthDate) {
    return undefined;
  }
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1;
  }
  return age >= 0 ? age : undefined;
};

const mapUser = (id: string, data: UnknownRecord): UserWithAdminMeta => ({
  id,
  ...(data as Omit<User, 'id'>),
  suspensionReason: asOptionalString(data.suspensionReason),
  suspendedAt: asDate(data.suspendedAt),
  suspendedBy: asOptionalString(data.suspendedBy),
  statusBeforeSuspension: asUserStatus(data.statusBeforeSuspension),
});

const getUserName = async (userId: string): Promise<string> => {
  if (!userId) {
    return '不明なユーザー';
  }
  const userSnap = await getDoc(doc(db, 'users', userId));
  const userData = userSnap.data();
  return asOptionalString(userData?.displayName) ?? '不明なユーザー';
};

const getPrefecturesForRegion = (region?: RegionName): string[] => {
  if (!region) {
    return [];
  }
  return [...REGION_PREFECTURES[region]];
};

const getAnnualIncomeScore = (value?: string): number => {
  if (!value) {
    return -1;
  }
  return ANNUAL_INCOME_OPTIONS.indexOf(value as (typeof ANNUAL_INCOME_OPTIONS)[number]);
};

const PROFILE_FILTER_KEYS: (keyof UserFilters)[] = [
  'region',
  'prefecture',
  'ageMin',
  'ageMax',
  'heightMin',
  'heightMax',
  'educationLevel',
  'annualIncomeMin',
  'bloodType',
  'bodyType',
  'maritalStatus',
  'donationMethod',
  'donationArea',
  'travelExpense',
  'identityDisclosure',
  'identityVerified',
  'educationVerified',
  'incomeVerified',
  'spermTestVerified',
  'stdTestVerified',
];

const hasValue = (value: unknown): boolean =>
  value !== undefined && value !== null && value !== '' && value !== false;

const needsProfileFiltering = (filters: UserFilters): boolean =>
  PROFILE_FILTER_KEYS.some((key) => hasValue(filters[key]));

const needsReviewFiltering = (filters: UserFilters): boolean =>
  filters.hasReviews === true || hasValue(filters.minReviewRating);

const fetchUserProfileFilterData = async (
  userId: string
): Promise<UserProfileFilterData | null> => {
  const profileSnap = await getDoc(doc(db, 'profiles', userId));
  if (!profileSnap.exists()) {
    return null;
  }

  const profileData = profileSnap.data() as UnknownRecord;
  const basicInfo = isRecord(profileData.basicInfo) ? profileData.basicInfo : {};
  const physicalInfo = isRecord(profileData.physicalInfo) ? profileData.physicalInfo : {};
  const donationPolicy = isRecord(profileData.donationPolicy) ? profileData.donationPolicy : {};
  const education = isRecord(basicInfo.education) ? basicInfo.education : {};
  const badges = isRecord(profileData.verificationBadges) ? profileData.verificationBadges : {};
  const birthDate = asDate(basicInfo.birthDate);

  return {
    residence: asOptionalString(basicInfo.residence),
    age: calculateAge(birthDate),
    height: asOptionalNumber(physicalInfo.height),
    educationLevel: asOptionalString(education.level),
    annualIncome: asOptionalString(basicInfo.annualIncome),
    bloodType: asOptionalString(basicInfo.bloodType),
    bodyType: asOptionalString(physicalInfo.bodyType),
    maritalStatus: asOptionalString(basicInfo.maritalStatus),
    donationMethods: asStringArray(donationPolicy.methods),
    donationAreas: asStringArray(donationPolicy.donationAreas),
    travelExpense: asOptionalString(donationPolicy.travelExpense),
    identityDisclosure: asOptionalString(donationPolicy.identityDisclosure),
    verifications: {
      identity: badges.identity === true,
      education: badges.education === true,
      income: badges.income === true,
      spermTest: badges.spermTest === true,
      stdTest: badges.stdTest === true,
    },
  };
};

const fetchReviewStats = async (userId: string): Promise<UserReviewStats> => {
  const reviewsSnap = await getDocs(query(collection(db, 'reviews'), where('toUserId', '==', userId)));
  if (reviewsSnap.empty) {
    return { averageRating: 0, totalCount: 0 };
  }

  let total = 0;
  reviewsSnap.forEach((reviewDoc) => {
    const data = reviewDoc.data() as UnknownRecord;
    total += typeof data.rating === 'number' ? data.rating : 0;
  });

  const totalCount = reviewsSnap.size;
  return {
    averageRating: Math.round((total / totalCount) * 10) / 10,
    totalCount,
  };
};

const matchesAdvancedFilters = (
  user: UserWithAdminMeta,
  filters: UserFilters,
  profile: UserProfileFilterData | null,
  reviewStats: UserReviewStats
): boolean => {
  if (filters.prefecture && profile?.residence !== filters.prefecture) {
    return false;
  }

  if (filters.region && !filters.prefecture) {
    const prefectures = getPrefecturesForRegion(filters.region);
    if (prefectures.length > 0 && (!profile?.residence || !prefectures.includes(profile.residence))) {
      return false;
    }
  }

  if (filters.ageMin !== undefined && ((profile?.age ?? -1) < filters.ageMin)) {
    return false;
  }
  if (filters.ageMax !== undefined && ((profile?.age ?? Number.MAX_SAFE_INTEGER) > filters.ageMax)) {
    return false;
  }
  if (filters.heightMin !== undefined && ((profile?.height ?? -1) < filters.heightMin)) {
    return false;
  }
  if (filters.heightMax !== undefined && ((profile?.height ?? Number.MAX_SAFE_INTEGER) > filters.heightMax)) {
    return false;
  }
  if (filters.educationLevel && profile?.educationLevel !== filters.educationLevel) {
    return false;
  }
  if (
    filters.annualIncomeMin &&
    getAnnualIncomeScore(profile?.annualIncome) < getAnnualIncomeScore(filters.annualIncomeMin)
  ) {
    return false;
  }
  if (filters.bloodType && profile?.bloodType !== filters.bloodType) {
    return false;
  }
  if (filters.bodyType && profile?.bodyType !== filters.bodyType) {
    return false;
  }
  if (filters.maritalStatus && profile?.maritalStatus !== filters.maritalStatus) {
    return false;
  }
  if (filters.donationMethod && !profile?.donationMethods.includes(filters.donationMethod)) {
    return false;
  }
  if (filters.donationArea && !profile?.donationAreas.includes(filters.donationArea)) {
    return false;
  }
  if (filters.travelExpense && profile?.travelExpense !== filters.travelExpense) {
    return false;
  }
  if (filters.identityDisclosure && profile?.identityDisclosure !== filters.identityDisclosure) {
    return false;
  }
  if (filters.hasReviews === true && reviewStats.totalCount === 0) {
    return false;
  }
  if (filters.minReviewRating !== undefined && reviewStats.averageRating < filters.minReviewRating) {
    return false;
  }
  if (filters.identityVerified === true && !(profile?.verifications.identity || user.identityVerified === true)) {
    return false;
  }
  if (filters.educationVerified === true && profile?.verifications.education !== true) {
    return false;
  }
  if (filters.incomeVerified === true && profile?.verifications.income !== true) {
    return false;
  }
  if (filters.spermTestVerified === true && profile?.verifications.spermTest !== true) {
    return false;
  }
  if (filters.stdTestVerified === true && profile?.verifications.stdTest !== true) {
    return false;
  }

  return true;
};

/**
 * ユーザー一覧取得
 */
export async function fetchUsers(
  filters: UserFilters = {},
  pageSize: number = 20,
  page: number = 1
): Promise<UserListResult> {
  try {
    const constraints: QueryConstraint[] = [];

    // フィルター適用
    if (filters.role) {
      constraints.push(where('role', '==', filters.role));
    }
    if (filters.status) {
      constraints.push(where('status', '==', filters.status));
    }

    const usersQuery = query(collection(db, 'users'), ...constraints);
    const snapshot = await getDocs(usersQuery);

    const users = snapshot.docs
      .map((userDoc) => mapUser(userDoc.id, userDoc.data() as UnknownRecord))
      .sort((a, b) => (b.createdAt?.toDate?.().getTime() ?? 0) - (a.createdAt?.toDate?.().getTime() ?? 0));

    // 検索クエリでフィルタリング（クライアント側）
    let filteredUsers = users;
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filteredUsers = users.filter(
        (user) =>
          user.email.toLowerCase().includes(query) ||
          user.displayName.toLowerCase().includes(query)
      );
    }

    if (filters.isDummy === 'only') {
      filteredUsers = filteredUsers.filter((user) => user.isDummy === true);
    } else if (filters.isDummy === 'exclude') {
      filteredUsers = filteredUsers.filter((user) => user.isDummy !== true);
    }

    const shouldFetchProfiles = needsProfileFiltering(filters);
    const shouldFetchReviews = needsReviewFiltering(filters);
    if (shouldFetchProfiles || shouldFetchReviews) {
      const defaultReviewStats: UserReviewStats = { averageRating: 0, totalCount: 0 };
      const advancedFilteredUsers = await Promise.all(
        filteredUsers.map(async (user) => {
          if (!user.id) {
            return null;
          }

          const [profile, reviewStats] = await Promise.all([
            shouldFetchProfiles ? fetchUserProfileFilterData(user.id) : Promise.resolve(null),
            shouldFetchReviews ? fetchReviewStats(user.id) : Promise.resolve(defaultReviewStats),
          ]);

          return matchesAdvancedFilters(user, filters, profile, reviewStats) ? user : null;
        })
      );

      filteredUsers = advancedFilteredUsers.filter(
        (user): user is UserWithAdminMeta => user !== null
      );
    }

    const totalCount = filteredUsers.length;
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    const startIndex = Math.max(0, (page - 1) * pageSize);
    const pagedUsers = filteredUsers.slice(startIndex, startIndex + pageSize);

    return {
      users: pagedUsers,
      totalCount,
      totalPages,
    };
  } catch (error) {
    console.error('Error fetching users:', error);
    throw new Error('ユーザー一覧の取得に失敗しました');
  }
}

/**
 * ユーザー詳細取得
 */
export async function fetchUserDetail(userId: string): Promise<UserWithAdminMeta> {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));

    if (!userDoc.exists()) {
      throw new Error('ユーザーが見つかりません');
    }

    const data = userDoc.data() as UnknownRecord;
    return mapUser(userDoc.id, data);
  } catch (error) {
    console.error('Error fetching user detail:', error);
    throw new Error('ユーザー詳細の取得に失敗しました');
  }
}

/**
 * ユーザーステータス更新
 */
export async function suspendUser(
  userId: string,
  reason: string,
  adminId: string
): Promise<void> {
  try {
    const normalizedReason = reason.trim();
    if (!normalizedReason) {
      throw new Error('停止理由を入力してください');
    }

    await runTransaction(db, async (transaction) => {
      const userRef = doc(db, 'users', userId);
      const userSnap = await transaction.get(userRef);
      if (!userSnap.exists()) {
        throw new Error('ユーザーが見つかりません');
      }

      const currentStatus = asUserStatus(userSnap.data().status) ?? 'email_verified';
      transaction.update(userRef, {
        status: 'suspended',
        statusBeforeSuspension: currentStatus === 'suspended' ? 'email_verified' : currentStatus,
        suspensionReason: normalizedReason,
        suspendedAt: serverTimestamp(),
        suspendedBy: adminId,
        updatedAt: serverTimestamp(),
      });
    });
  } catch (error) {
    console.error('Error suspending user:', error);
    throw new Error(error instanceof Error ? error.message : 'アカウント停止に失敗しました');
  }
}

export async function restoreSuspendedUser(userId: string, adminId: string): Promise<void> {
  try {
    await runTransaction(db, async (transaction) => {
      const userRef = doc(db, 'users', userId);
      const userSnap = await transaction.get(userRef);
      if (!userSnap.exists()) {
        throw new Error('ユーザーが見つかりません');
      }

      const data = userSnap.data() as UnknownRecord;
      const previousStatus = asUserStatus(data.statusBeforeSuspension);
      transaction.update(userRef, {
        status: previousStatus && previousStatus !== 'suspended' ? previousStatus : 'email_verified',
        statusBeforeSuspension: null,
        suspensionReason: null,
        suspendedAt: null,
        suspendedBy: null,
        restoredAt: serverTimestamp(),
        restoredBy: adminId,
        updatedAt: serverTimestamp(),
      });
    });
  } catch (error) {
    console.error('Error restoring user:', error);
    throw new Error(error instanceof Error ? error.message : 'アカウント停止解除に失敗しました');
  }
}

export async function fetchUserOperationDetail(
  userId: string
): Promise<AdminUserOperationDetail> {
  const [
    user,
    profileSnap,
    privateProfileSnap,
    verificationSnap,
    inquiriesSnap,
    receivedRequestsSnap,
    sentRequestsSnap,
    conversationsSnap,
    receivedReviewsSnap,
    sentReviewsSnap,
    notificationsSnap,
  ] = await Promise.all([
    fetchUserDetail(userId),
    getDoc(doc(db, 'profiles', userId)),
    getDoc(doc(db, 'privateProfiles', userId)),
    getDoc(doc(db, 'verifications', userId)),
    getDocs(query(collection(db, 'contactInquiries'), where('userId', '==', userId))),
    getDocs(
      query(
        collection(db, 'profileAccessRequests'),
        where('toUserId', '==', userId),
        orderBy('requestedAt', 'desc'),
        limit(20)
      )
    ),
    getDocs(
      query(
        collection(db, 'profileAccessRequests'),
        where('fromUserId', '==', userId),
        orderBy('requestedAt', 'desc'),
        limit(20)
      )
    ),
    getDocs(
      query(
        collection(db, 'conversations'),
        where('participants', 'array-contains', userId),
        orderBy('lastMessageAt', 'desc'),
        limit(20)
      )
    ),
    getDocs(query(collection(db, 'reviews'), where('toUserId', '==', userId), limit(50))),
    getDocs(query(collection(db, 'reviews'), where('fromUserId', '==', userId), limit(50))),
    getDocs(
      query(
        collection(db, 'notifications', userId, 'logs'),
        orderBy('sentAt', 'desc'),
        limit(20)
      )
    ),
  ]);

  const profileData = profileSnap.exists() ? (profileSnap.data() as UnknownRecord) : null;
  const basicInfo = isRecord(profileData?.basicInfo) ? profileData.basicInfo : {};
  const birthDate = asDate(basicInfo.birthDate);
  const privateProfileData = privateProfileSnap.exists()
    ? (privateProfileSnap.data() as UnknownRecord)
    : null;
  const verificationData = verificationSnap.exists()
    ? (verificationSnap.data() as UnknownRecord)
    : null;

  const requestDocs = [
    ...receivedRequestsSnap.docs.map((requestDoc) => ({ requestDoc, direction: 'received' as const })),
    ...sentRequestsSnap.docs.map((requestDoc) => ({ requestDoc, direction: 'sent' as const })),
  ];
  const accessRequests = await Promise.all(
    requestDocs.map(async ({ requestDoc, direction }) => {
      const data = requestDoc.data() as UnknownRecord;
      const counterpartyId =
        direction === 'received'
          ? asOptionalString(data.fromUserId) ?? ''
          : asOptionalString(data.toUserId) ?? '';

      return {
        id: requestDoc.id,
        direction,
        counterpartyId,
        counterpartyName: await getUserName(counterpartyId),
        status: asOptionalString(data.status) ?? 'unknown',
        message: asOptionalString(data.message),
        requestedAt: asDate(data.requestedAt),
        respondedAt: asDate(data.respondedAt),
      } satisfies AdminUserAccessRequest;
    })
  );

  const conversations = await Promise.all(
    conversationsSnap.docs.map(async (conversationDoc) => {
      const data = conversationDoc.data() as UnknownRecord;
      const participants = Array.isArray(data.participants)
        ? data.participants.filter((item): item is string => typeof item === 'string')
        : [];
      const counterpartyId = participants.find((id) => id !== userId) ?? '';
      const unreadBy = isRecord(data.unreadBy) ? data.unreadBy : {};
      const unreadValue = unreadBy[userId];

      return {
        id: conversationDoc.id,
        counterpartyId,
        counterpartyName: await getUserName(counterpartyId),
        lastMessage: asOptionalString(data.lastMessage),
        lastMessageAt: asDate(data.lastMessageAt),
        unreadCount: typeof unreadValue === 'number' ? unreadValue : 0,
      } satisfies AdminUserConversation;
    })
  );

  const reviewDocs = [
    ...receivedReviewsSnap.docs.map((reviewDoc) => ({ reviewDoc, direction: 'received' as const })),
    ...sentReviewsSnap.docs.map((reviewDoc) => ({ reviewDoc, direction: 'sent' as const })),
  ];
  const reviews = await Promise.all(
    reviewDocs.map(async ({ reviewDoc, direction }) => {
      const data = reviewDoc.data() as UnknownRecord;
      const counterpartyId =
        direction === 'received'
          ? asOptionalString(data.fromUserId) ?? ''
          : asOptionalString(data.toUserId) ?? '';

      return {
        id: reviewDoc.id,
        direction,
        counterpartyId,
        counterpartyName: await getUserName(counterpartyId),
        rating: typeof data.rating === 'number' ? data.rating : 0,
        comment: asOptionalString(data.comment),
        createdAt: asDate(data.createdAt),
      } satisfies AdminUserReview;
    })
  );

  return {
    user,
    profile: profileData
      ? {
          residence: asOptionalString(basicInfo.residence),
          birthDate,
          age: calculateAge(birthDate),
          basicInfo,
          physicalInfo: isRecord(profileData.physicalInfo) ? profileData.physicalInfo : undefined,
          donationPolicy: isRecord(profileData.donationPolicy) ? profileData.donationPolicy : undefined,
          doneePolicy: isRecord(profileData.doneePolicy) ? profileData.doneePolicy : undefined,
        }
      : null,
    privateProfile: privateProfileData
      ? {
          freeText: asOptionalString(privateProfileData.freeText),
          images: Array.isArray(privateProfileData.images)
            ? privateProfileData.images.filter((item): item is string => typeof item === 'string')
            : [],
        }
      : null,
    verification: verificationData
      ? {
          adminReview: isRecord(verificationData.adminReview) ? verificationData.adminReview : undefined,
          items: ['identity', 'education', 'income', 'spermTest', 'stdTest'].reduce<
            AdminUserVerification['items']
          >((items, key) => {
            const value = isRecord(verificationData[key]) ? verificationData[key] : {};
            items[key] = {
              status: asOptionalString(value.status) ?? 'none',
              documents: Array.isArray(value.documents) ? value.documents.length : 0,
              rank: asOptionalString(value.rank),
              expiresAt: asDate(value.expiresAt),
            };
            return items;
          }, {}),
        }
      : null,
    inquiries: inquiriesSnap.docs
      .map((inquiryDoc) => {
        const data = inquiryDoc.data() as UnknownRecord;
        return {
          id: inquiryDoc.id,
          category: asOptionalString(data.category) ?? 'other',
          status: asOptionalString(data.status) ?? 'open',
          message: asOptionalString(data.message) ?? '',
          createdAt: asDate(data.createdAt),
        } satisfies AdminUserInquiry;
      })
      .sort((a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0)),
    accessRequests,
    conversations,
    reviews: reviews.sort((a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0)),
    notifications: notificationsSnap.docs.map((notificationDoc) => {
      const data = notificationDoc.data() as UnknownRecord;
      return {
        id: notificationDoc.id,
        type: asOptionalString(data.type) ?? 'unknown',
        title: asOptionalString(data.title) ?? '通知',
        status: isRecord(data.status) ? data.status : undefined,
        sentAt: asDate(data.sentAt),
        readAt: asDate(data.readAt),
      } satisfies AdminUserNotification;
    }),
  };
}

/**
 * ユーザー統計情報取得
 */
export async function fetchUserStats() {
  try {
    const usersRef = collection(db, 'users');

    // 全ユーザー数
    const allUsersSnapshot = await getDocs(query(usersRef));
    const totalUsers = allUsersSnapshot.size;

    // ドナー数
    const donorsSnapshot = await getDocs(query(usersRef, where('role', '==', 'donor')));
    const totalDonors = donorsSnapshot.size;

    // ドニー数
    const doneesSnapshot = await getDocs(query(usersRef, where('role', '==', 'donee')));
    const totalDonees = doneesSnapshot.size;

    // 審査待ち数
    const pendingSnapshot = await getDocs(
      query(usersRef, where('status', '==', 'under_review'))
    );
    const pendingReviews = pendingSnapshot.size;

    // 今日の新規登録
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayTimestamp = Timestamp.fromDate(today);

    const todayUsersSnapshot = await getDocs(
      query(usersRef, where('createdAt', '>=', todayTimestamp))
    );
    const todayRegistrations = todayUsersSnapshot.size;

    return {
      totalUsers,
      totalDonors,
      totalDonees,
      pendingReviews,
      todayRegistrations,
    };
  } catch (error) {
    console.error('Error fetching user stats:', error);
    throw new Error('統計情報の取得に失敗しました');
  }
}
